import type { BuildSeriesKeyFn } from '@gonfalon/dataviz-experimental/utils/chartDataTransformer';
import { durationToMilliseconds } from '@gonfalon/datetime/durationToMilliseconds';
import type { ChartDataResponse, SuggestionResponse } from '@gonfalon/openapi/types';
import { fromAbsolute, getLocalTimeZone } from '@internationalized/date';

import { EDITABLE_EVAL_KEY_PREFIX } from '#components/AiConfigs/constants';
import type { WindowKey } from '#components/TimePager/useTimePager';

import type { SelectField } from './constants';
import { DURATION_PRESETS } from './constants';
import type { DimensionOption, TableDimensionOption } from './types';

export function parseJudgeSuggestionItems(response: SuggestionResponse): string[] {
  const keyValues: Array<{ count: number; value: string }> =
    response.items
      .find((item) => item.field === 'key')
      ?.sampleValues?.map((sv) => ({ count: sv.count, value: sv.value as string })) ?? [];

  return (
    keyValues
      ?.filter((keyValue) => keyValue.value.startsWith(EDITABLE_EVAL_KEY_PREFIX))
      .sort((a, b) => b.count - a.count)
      .map((keyValue) => keyValue.value) ?? []
  );
}

/** Chart Data API `select` entries for judge averages and counts from metric ids using `EDITABLE_EVAL_KEY_PREFIX`. */
export function judgeMetricIdsToSelectFields(judgeMetricIds: string[]): SelectField[] {
  const seen = new Set<string>();
  const result: SelectField[] = [];

  for (const id of judgeMetricIds) {
    if (!id.startsWith(EDITABLE_EVAL_KEY_PREFIX)) {
      continue;
    }

    for (const expression of ['avg', 'count'] as const) {
      const key = `${id}|${expression}|`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      result.push({ field: id, expression });
    }
  }

  return result;
}

/** Row shape produced by transformTableData; may include sub-rows (config: variations; agent graph: configs / nodes) */
export type TrendsExplorerTableRow = Record<string, string | number | boolean | string[] | null | undefined> & {
  variationRows?: TrendsExplorerTableRow[];
  rowId?: string;
  variationKey?: string;
  /** Human-readable label for variation rows when resolved from dimensionOptions */
  variationLabel?: string;
  isVariationRow?: boolean;
  /** Optional smaller description shown below the first column value */
  dimensionDescription?: string;
  /** Display name for model_name (resolved from modelNameByKey) */
  model_name?: string;
};

/** Label when a config has variations with different providers or models */
export const MULTIPLE_LABEL = 'Multiple';

function applyGroupByValuesToRow(
  row: TrendsExplorerTableRow,
  item: ChartDataResponse['items'][number],
  groupByFields: ChartDataResponse['group_by_fields'],
  columns: Array<{ label: string; key: string }>,
): void {
  if (!groupByFields?.length) {
    return;
  }
  groupByFields.forEach((f, i) => {
    if (f?.name && columns.some((c) => c.key === f.name)) {
      const v = item.group_by_values?.[i];
      // eslint-disable-next-line no-param-reassign
      row[f.name] = v ? String(v) : null;
    }
  });
}

/**
 * From a list of rows (e.g. child items or variation subrows), derive a single display value for
 * a dimension field: one value if all same, or MULTIPLE_LABEL if multiple. Used for parent row
 * provider/model when grouping by config (from variation rows), or model when grouping by provider, etc.
 */
function getProviderOrModelForParentRow(rows: Array<Record<string, unknown>>, field: 'provider_name' | 'model_name') {
  const values = rows.map((r) => r[field]);
  const unique = [...new Set(values)];
  if (unique.length === 0) {
    return null;
  }
  if (unique.length === 1) {
    return unique[0];
  }
  return MULTIPLE_LABEL;
}

/** Resolve row.model_name to display name when modelNameByKey is provided (same pattern as config names). */
function resolveModelName(row: TrendsExplorerTableRow, modelNameByKey?: Record<string, string>): void {
  if (!modelNameByKey || !row.model_name) {
    return;
  }
  const label = modelNameByKey[row.model_name];
  if (label !== undefined) {
    // eslint-disable-next-line no-param-reassign
    row.model_name = label;
  }
}

export const getTransformOptions = (isSatisfactionChart: boolean, seriesKeyBuilder?: BuildSeriesKeyFn) => {
  return isSatisfactionChart
    ? {
        xAxisDataKey: 'timestamp',
        seriesKeyBuilder,
        additionalFields: {
          feedback_user_positive_count: '_positive',
          feedback_user_negative_count: '_negative',
        },
      }
    : {
        xAxisDataKey: 'timestamp',
        seriesKeyBuilder,
      };
};

// Takes in a hex color and returns a faded version of it
export const fadeColor = (color: string, factor = 0.5) => {
  const hex = color.replace(/^#/, '');
  const r = hex.length === 3 ? parseInt(hex.slice(0, 1) + hex.slice(0, 1), 16) : parseInt(hex.slice(0, 2), 16);
  const g = hex.length === 3 ? parseInt(hex.slice(1, 2) + hex.slice(1, 2), 16) : parseInt(hex.slice(2, 4), 16);
  const b = hex.length === 3 ? parseInt(hex.slice(2, 3) + hex.slice(2, 3), 16) : parseInt(hex.slice(4, 6), 16);
  const newR = Math.round(r + (255 - r) * factor);
  const newG = Math.round(g + (255 - g) * factor);
  const newB = Math.round(b + (255 - b) * factor);
  return `#${[newR, newG, newB].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
};

export const getPreset = (startMs: number, endMs: number): WindowKey | null => {
  const tz = getLocalTimeZone();
  const relativeTo = fromAbsolute(endMs, tz);
  const windowMs = endMs - startMs;

  for (const preset of DURATION_PRESETS) {
    try {
      const presetMs = durationToMilliseconds(preset, { relativeTo });
      if (Math.abs(presetMs - windowMs) < 60_000) {
        return preset;
      }
    } catch {
      // skip presets that need relativeTo and fail
    }
  }
  return null;
};

function itemToRow(
  item: ChartDataResponse['items'][number],
  dimensionKey: string,
  dimensionLabel: string,
  fields: ChartDataResponse['fields'],
  columns: Array<{ label: string; key: string }>,
  variationKey?: string,
  dimensionDescription?: string,
  variationLabel?: string,
): TrendsExplorerTableRow {
  const row: TrendsExplorerTableRow = {};
  row.dimension = dimensionLabel;
  row.dimensionDescription = dimensionDescription;
  row.rowId = dimensionKey;
  if (variationKey) {
    row.variationKey = variationKey;
  }
  if (variationLabel !== undefined) {
    row.variationLabel = variationLabel;
  }
  const dataRow = item.data?.[0];
  if (dataRow) {
    fields.forEach((f, i) => {
      if (columns.some((c) => c.key === f.name)) {
        row[f.name] = dataRow[i] ?? null;
      }
    });
  }
  return row;
}

/**
 * Coerce a cell/table value to a number for aggregation, sorting, or styling.
 * null/undefined → 0; valid number → as-is; string → parsed or 0; otherwise → 0.
 */
export function toNumber(value: unknown): number {
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
}

const getNonConfigDimensionDescription = (
  dimension: string,
  dimensionKey: string,
  dimensionOption?: DimensionOption,
): string | undefined => {
  if (dimension === '') {
    // none
    return 'Aggregate metrics across all configs';
  }
  if (!dimensionOption) {
    return undefined;
  }
  if (dimension === 'provider_name') {
    return `All ${dimensionOption.label ?? dimensionKey} models`;
  }
};

type ChartDataItem = ChartDataResponse['items'][number];

/** True when the primary group_by is config or agent graph, so we show expandable sub-rows. Provider/model are one row each, not expandable. */
function isConfigDimension(groupByFields: ChartDataResponse['group_by_fields']): boolean {
  const primary = groupByFields?.[0]?.name;
  return primary === 'config_key' || primary === 'graph_key';
}

/**
 * Build table rows from a rollup response.
 * - Config: one row per config with expandable variation subrows (when multiple variations).
 * - Agent graph: one row per graph with expandable config (node) subrows (when multiple configs).
 * - Provider/model: one row per provider or model; no subrows (rollup row only).
 */
function buildRowsFromRollupResponse(
  items: ChartDataItem[],
  groupByFields: ChartDataResponse['group_by_fields'],
  dimensionOptions: TableDimensionOption[],
  fields: ChartDataResponse['fields'],
  columns: Array<{ label: string; key: string }>,
  modelNameByKey?: Record<string, string>,
  configNameByKey?: Record<string, string>,
): TrendsExplorerTableRow[] {
  const rollupItems = items.filter((item) => !item.rollup_key);
  const childItems = items.filter((item) => item.rollup_key);

  if (rollupItems.length === 0) {
    return [];
  }

  const primaryDimensionIndex = 0;
  const secondaryDimensionIndex = 1;
  const hasSecondaryGroupBy = (groupByFields?.length ?? 0) > 1;
  const primaryGroupName = groupByFields?.[primaryDimensionIndex]?.name;
  const secondaryGroupName = groupByFields?.[secondaryDimensionIndex]?.name;
  const isVariationSubrow = secondaryGroupName === 'variation_key';
  const isConfigSubrowForAgentGraph = primaryGroupName === 'graph_key' && secondaryGroupName === 'config_key';
  const allowSubrows = isConfigDimension(groupByFields);

  const result: TrendsExplorerTableRow[] = [];
  for (const rollupItem of rollupItems) {
    const dimensionKey = String(rollupItem.group_by_values?.[primaryDimensionIndex] ?? '');
    const dimensionOption = dimensionOptions.find((o) => o.value === dimensionKey);
    const dimensionLabel = dimensionOption?.label ?? dimensionKey ?? '';
    const dimensionDescription =
      dimensionOption?.description ??
      getNonConfigDimensionDescription(String(groupByFields?.[0]?.name ?? ''), dimensionKey, dimensionOption);

    const parentRow = itemToRow(
      rollupItem,
      dimensionKey,
      dimensionLabel,
      fields,
      columns,
      undefined,
      dimensionDescription,
    );
    applyGroupByValuesToRow(parentRow, rollupItem, groupByFields ?? [], columns);
    parentRow.rowId = dimensionKey;
    resolveModelName(parentRow, modelNameByKey);

    const children = childItems.filter((c) => c.rollup_key === rollupItem.key);
    const childRows: TrendsExplorerTableRow[] = children.map((item) => {
      const secondaryValue = hasSecondaryGroupBy ? item.group_by_values?.[secondaryDimensionIndex] : undefined;
      let variationKey: string | undefined;
      let variationLabel: string | undefined;
      if (isVariationSubrow && secondaryValue) {
        variationKey = String(secondaryValue);
        variationLabel =
          dimensionOption?.variations?.find((v) => v.value === variationKey)?.label ?? variationKey ?? undefined;
      } else if (isConfigSubrowForAgentGraph && secondaryValue) {
        variationKey = String(secondaryValue);
        variationLabel = configNameByKey?.[variationKey] ?? variationKey;
      }
      const row = itemToRow(
        item,
        dimensionKey,
        dimensionLabel,
        fields,
        columns,
        variationKey,
        dimensionDescription,
        variationLabel,
      );
      applyGroupByValuesToRow(row, item, groupByFields ?? [], columns);
      resolveModelName(row, modelNameByKey);
      const groupValues = (item.group_by_values ?? []).slice(0, groupByFields?.length ?? 1);
      row.rowId =
        groupValues
          .map((v) => (v ? String(v) : ''))
          .filter(Boolean)
          .join('\n') || dimensionKey;
      return row;
    });

    const parentProvider = getProviderOrModelForParentRow(childRows, 'provider_name');
    if (parentProvider !== null) {
      parentRow.provider_name = String(parentProvider);
    }
    const parentModel = getProviderOrModelForParentRow(childRows, 'model_name');
    if (parentModel) {
      parentRow.model_name = String(parentModel);
    }

    if (allowSubrows && childRows.length > 1) {
      parentRow.variationRows = childRows;
    }

    result.push(parentRow);
  }
  return result;
}

export const transformTableData = (
  tableData: ChartDataResponse | null,
  dimensionOptions: TableDimensionOption[],
  columns: Array<{ label: string; key: string }>,
  modelNameByKey?: Record<string, string>,
  /** Resolved display names for `config_key` sub-rows when grouping agent graphs by config (node). */
  configNameByKey?: Record<string, string>,
): TrendsExplorerTableRow[] => {
  if (!tableData?.items?.length) {
    return [];
  }
  const { fields, group_by_fields: groupByFields, items } = tableData;

  const hasRollupResponse = items.some((item) => item.rollup_key !== undefined);
  if (hasRollupResponse) {
    return buildRowsFromRollupResponse(
      items,
      groupByFields ?? [],
      dimensionOptions,
      fields,
      columns,
      modelNameByKey,
      configNameByKey,
    );
  }

  // No rollup: flat list (e.g. dimension 'none' or API not using rollup)
  return items.map((item) => {
    const dimension = String(groupByFields?.[0]?.name ?? '');
    const dimensionKey = String(item.group_by_values?.[0] ?? '');
    const dimensionOption = dimensionOptions.find((o) => o.value === dimensionKey);
    const dimensionLabel = dimensionOption?.label ?? dimensionKey ?? '';
    const dimensionDescription = getNonConfigDimensionDescription(dimension, dimensionKey, dimensionOption);
    const row = itemToRow(item, dimensionKey, dimensionLabel, fields, columns, undefined, dimensionDescription);
    applyGroupByValuesToRow(row, item, groupByFields ?? [], columns);
    resolveModelName(row, modelNameByKey);
    return row;
  });
};
